import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import type { AppleSignInResponse, SSOResponseData } from '@freelancer/auth';
import { SSOUser } from '@freelancer/auth';
import type { FacebookAuthResponse } from '@freelancer/facebook';
import { ResponseData } from '@freelancer/freelancer-http';
import type { GoogleAuthResponse } from '@freelancer/google-sign-in';
import type { UserCheckError } from '@freelancer/login-signup';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { InputSize, InputType } from '@freelancer/ui/input';
import { LinkUnderline } from '@freelancer/ui/link';
import { LogoSize } from '@freelancer/ui/logo';
import { TextSize } from '@freelancer/ui/text';
import type { User } from '@freelancer/ui/user-avatar';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import type { CredentialsFormGroup } from '../../../new-account/new-account.types';
import type { SignupDetailsFormGroup } from '../../signup.types';

@Component({
  standalone: false,
  selector: 'app-recovery-email-form',
  template: `
    @if (!newAccountFlow) {
      <app-form-logo
        [showBackButton]="true"
        [backTrackingLabel]="providerTrackingLabelPrefix + 'Signup-Back'"
        [size]="logoSize"
        (back)="back.emit()"
      ></app-form-logo>
    }
    <form
      [class.NewAccountForm]="newAccountFlow"
      [class.RecoveryEmailForm]="!newAccountFlow"
      [formGroup]="formGroup"
      (ngSubmit)="onSubmit()"
    >
      @if (!newAccountFlow) {
        <h2
          class="text-mid RecoveryEmailForm-heading mb-small"
          i18n="Sign Up action heading"
        >
          Sign Up
        </h2>
      }
      @if (avatarUser && !newAccountFlow) {
        <div class="RecoveryEmailForm-picture mb-small">
          <fl-user-avatar [users]="[avatarUser]"></fl-user-avatar>
        </div>
      }
      @if (!newAccountFlow) {
        <h2
          class="text-mid RecoveryEmailForm-heading mb-mid"
          i18n="Welcoming user heading"
        >
          Welcome {{ user?.name }}!
        </h2>
      }
      @if (!this.user?.email) {
        <p class="mb-xsmall">
          @if (newAccountFlow) {
            <ng-container i18n="Login form backup email explanation">
              Provide a backup email to avoid duplicate accounts and get
              notified of any account issues.
            </ng-container>
          }
          @if (!newAccountFlow) {
            <ng-container i18n="Login form backup email explanation">
              Please supply a backup email. We'll make sure you don't create
              duplicate accounts and will use this email to let you know if
              there's an issue with your account.
            </ng-container>
          }
        </p>
      }
      @if (this.user?.email) {
        <p
          class="mb-xsmall"
          i18n="Detail text to create an account using the email in the input"
        >
          Create an account as:
        </p>
      }
      <fl-input
        class="mb-small"
        flTrackingLabel="{{ providerTrackingLabelPrefix }}Signup-EmailInput"
        placeholder="Email"
        label="Recovery email"
        i18n-label="Recovery email input label"
        i18n-placeholder="Login form email/username field placeholder"
        [disabled]="!!this.user?.email"
        [iconEnd]="!!this.user?.email ? 'ui-lock-closed' : ''"
        [control]="formGroup.controls.email"
        [size]="InputSize.LARGE"
        [type]="InputType.EMAIL"
      ></fl-input>
      @if (isSignupDetailsForm(formGroup)) {
        @if (formGroup.controls.termsOfUse; as control) {
          <div class="Agreement">
            <fl-checkbox
              class="mb-small"
              flTrackingLabel="{{
                providerTrackingLabelPrefix
              }}Signup-TermsOfUseCheckbox"
              [control]="control"
              [id]="providerTrackingLabelPrefix + 'SignupAgreement'"
            ></fl-checkbox>
            <fl-label [for]="providerTrackingLabelPrefix + 'SignupAgreement'">
              <p
                class="mb-small text-xxsmall text-left {{
                  control.dirty && control.invalid ? 'text-error' : 'text-dark'
                }}"
                i18n="User agreement to terms of use and privacy policy"
              >
                I agree to the Freelancer
                <fl-link
                  flTrackingLabel="{{
                    providerTrackingLabelPrefix
                  }}Signup-TermsOfUseLink"
                  [link]="'/info/plainterms.html'"
                  [newTab]="true"
                  [underline]="LinkUnderline.ALWAYS"
                  [size]="TextSize.INHERIT"
                  >User Agreement</fl-link
                >
                and
                <fl-link
                  flTrackingLabel="{{
                    providerTrackingLabelPrefix
                  }}Signup-PrivacyPolicyLink"
                  [link]="'/about/privacy'"
                  [newTab]="true"
                  [size]="TextSize.INHERIT"
                  [underline]="LinkUnderline.ALWAYS"
                  >Privacy Policy</fl-link
                >.
              </p>
            </fl-label>
          </div>
        }
      }
      @if (response && response.status === 'error') {
        <app-login-signup-errors
          class="mb-small"
          [response]="response"
          (switchToLogin)="switchToLogin.emit()"
        ></app-login-signup-errors>
      }
      @if (newAccountFlow) {
        <app-login-signup-button
          class="NewAccountForm-button mb-small"
          i18n="Recovery email form button"
          [color]="ButtonColor.PRIMARY"
          [sizeDesktop]="ButtonSize.XLARGE"
          [sizeTablet]="ButtonSize.LARGE"
          [stepLabel]="'RecoveryEmailForm'"
        >
          Continue
        </app-login-signup-button>
      }
      @if (!newAccountFlow) {
        <app-login-signup-button
          i18n="Login form button (this is an action)"
          [stepLabel]="providerTrackingLabelPrefix + 'Signup'"
        >
          Join Freelancer
        </app-login-signup-button>
      }
    </form>
  `,
  styleUrls: ['./recovery-email-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoveryEmailFormComponent implements OnChanges {
  TextSize = TextSize;
  InputSize = InputSize;
  InputType = InputType;
  LinkUnderline = LinkUnderline;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;

  avatarUser?: User;
  providerTrackingLabelPrefix: string;

  @Input() logoSize = LogoSize.MID;
  @Input() formGroup: SignupDetailsFormGroup | CredentialsFormGroup;
  @Input() user?: SSOUser;
  @Input() response: ResponseData<unknown, UserCheckError>;
  @Input() ssoAuth:
    | SSOResponseData<'facebook', FacebookAuthResponse>
    | SSOResponseData<'apple', AppleSignInResponse>
    | SSOResponseData<'google', GoogleAuthResponse>;
  @Input() newAccountFlow = false;

  @Output() complete = new EventEmitter();
  @Output() switchToLogin = new EventEmitter();
  @Output() back = new EventEmitter();

  ngOnChanges(changes: SimpleChanges): void {
    if ('ssoAuth' in changes && this.ssoAuth) {
      this.providerTrackingLabelPrefix =
        this.ssoAuth.provider.charAt(0).toUpperCase() +
        this.ssoAuth.provider.slice(1);
    }
    if ('user' in changes) {
      this.avatarUser = this.user
        ? {
            username: this.user.name,
            avatar: this.user.profileUrl,
          }
        : undefined;
      if (this.user) {
        if (this.user.email) {
          this.formGroup.patchValue({ email: this.user.email });
        }
        if (this.user.lastName) {
          this.formGroup.patchValue({ lastName: this.user.lastName });
        }
        this.formGroup.patchValue({ firstName: this.user.name });
      }
    }
  }

  isSignupDetailsForm(
    formGroup: SignupDetailsFormGroup | CredentialsFormGroup,
  ): formGroup is SignupDetailsFormGroup {
    return 'termsOfUse' in formGroup.controls;
  }

  onSubmit(): void {
    const { email } = this.formGroup.controls;
    const termsOfUse = this.isSignupDetailsForm(this.formGroup)
      ? this.formGroup.controls.termsOfUse
      : undefined;

    dirtyAndValidate(this.formGroup);

    // Make sure the user acknowledged the terms of use before they proceed
    // only when the form contains the terms of use control.
    if (email.valid && (!termsOfUse || termsOfUse.valid)) {
      this.complete.emit();
    }
  }
}
